import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ScienceIcon from '@mui/icons-material/Science';
import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { fetchTutorProfile, addTutorReview, updateTutorReview } from '../../api/tutorProfile';
import { SUBJECT_OPTIONS } from '../../components/forms/StudentFormDialog';
import useAuthStore from '../../store/authStore';
import ReviewSortControls from '../../components/reviews/ReviewSortControls';
import sortReviews from '../../components/reviews/sortReviews';

const B = { primary: '#4357AD', dark: '#1E2A4A', green: '#D3FEC6' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const translateSubject = (val) => {
  if (!val) return '';
  const byValue = SUBJECT_OPTIONS.find((o) => o.value === val);
  if (byValue) return byValue.label;
  const byLabel = SUBJECT_OPTIONS.find((o) => o.label === val);
  if (byLabel) return byLabel.label;
  // Handle comma-separated values — pick first translatable part
  if (val.includes(',')) {
    const parts = val.split(',').map((s) => s.trim());
    const translated = parts.find((p) => SUBJECT_OPTIONS.find((o) => o.value === p || o.label === p));
    if (translated) {
      const opt = SUBJECT_OPTIONS.find((o) => o.value === translated || o.label === translated);
      return opt?.label || translated;
    }
  }
  return val;
};

const profileSections = [
  { key: 'specialization', label: 'Спеціалізація', icon: <SchoolIcon />, color: '#8B5CF6' },
  { key: 'grades', label: 'Класи', icon: <SchoolIcon />, color: '#06B6D4' },
  { key: 'education', label: 'Освіта', icon: <AutoStoriesIcon />, color: '#F59E0B' },
  { key: 'experience', label: 'Досвід викладання', icon: <WorkIcon />, color: '#22C55E' },
];

const textSections = [
  { key: 'bio', label: 'Про себе', icon: <FormatQuoteIcon />, color: B.primary },
  { key: 'scientificInterests', label: 'Наукові інтереси', icon: <ScienceIcon />, color: '#8B5CF6' },
  { key: 'workRules', label: 'Правила роботи', icon: <GavelIcon />, color: '#EF4444' },
];

const emptyForm = { studentName: '', rating: 5, text: '' };

const ParentTutorProfilePage = () => {
  const { tutorId } = useParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [reviewForm, setReviewForm] = useState(emptyForm);
  const [reviewSort, setReviewSort] = useState('newest');
  const [showForm, setShowForm] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['tutor-profile', tutorId],
    queryFn: () => fetchTutorProfile(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });

  const addMutation = useMutation({
    mutationFn: (payload) => addTutorReview(tutorId, payload),
    onSuccess: () => {
      enqueueSnackbar('Відгук додано', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setReviewForm(emptyForm);
      setShowForm(false);
    },
    onError: () => enqueueSnackbar('Не вдалося додати відгук', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ reviewId, payload }) => updateTutorReview(reviewId, payload),
    onSuccess: () => {
      enqueueSnackbar('Відгук оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setReviewForm(emptyForm);
      setEditingReviewId(null);
      setShowForm(false);
    },
    onError: () => enqueueSnackbar('Не вдалося оновити відгук', { variant: 'error' }),
  });

  const handleSubmitReview = () => {
    if (!reviewForm.studentName.trim() || !reviewForm.text.trim()) {
      enqueueSnackbar('Заповніть усі поля', { variant: 'warning' });
      return;
    }
    const payload = { studentName: reviewForm.studentName, rating: reviewForm.rating, text: reviewForm.text };
    if (editingReviewId) {
      updateMutation.mutate({ reviewId: editingReviewId, payload });
    } else {
      addMutation.mutate(payload);
    }
  };

  const handleEditReview = (review) => {
    setReviewForm({ studentName: review.studentName, rating: review.rating, text: review.text });
    setEditingReviewId(review.id);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setReviewForm(emptyForm);
    setEditingReviewId(null);
    setShowForm(false);
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
  }
  if (error) {
    return <Typography color="error">Помилка: {error?.response?.data?.message || error.message}</Typography>;
  }
  if (!profile) {
    return <Typography>Профіль не знайдено.</Typography>;
  }

  const completionRate = Math.round(profile.completionRate ?? 0);
  const initials = profile.tutorName
    ?.split(' ')
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  return (
    <Stack spacing={2.5}>
      {/* Hero Card */}
      <Card
        sx={{
          ...cardSx,
          background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 60%, #6C63FF 100%)`,
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ py: 4, px: { xs: 3, md: 5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={3}>
            <Box sx={{ flexShrink: 0, alignSelf: { xs: 'center', md: 'flex-start' } }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 36,
                  border: '4px solid rgba(255,255,255,0.25)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                {initials}
              </Avatar>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800} sx={{ color: '#fff', mb: 0.5 }}>
                {profile.tutorName}
              </Typography>
              {profile.subject && (
                <Chip
                  label={translateSubject(profile.subject)}
                  sx={{
                    bgcolor: 'rgba(211,254,198,0.2)',
                    color: B.green,
                    fontWeight: 700,
                    fontSize: 13,
                    border: '1px solid rgba(211,254,198,0.3)',
                    mb: 1,
                  }}
                />
              )}
              <Stack direction="row" spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                {[
                  { value: profile.completedLessons ?? 0, label: 'Проведено' },
                  { value: profile.averageRating ? profile.averageRating.toFixed(1) : '—', label: 'Рейтинг' },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', lineHeight: 1 }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {[
          { label: 'Проведених уроків', value: profile.completedLessons ?? 0, icon: <CheckCircleIcon />, color: '#22C55E', bg: '#D1FAE5' },
          { label: 'Середній рейтинг', value: profile.averageRating ? profile.averageRating.toFixed(1) : '—', icon: <StarIcon />, color: '#EF4444', bg: '#FEE2E2', extra: profile.averageRating > 0 && <Rating value={profile.averageRating} precision={0.1} readOnly size="small" sx={{ ml: 1 }} /> },
        ].map((s) => (
          <Card key={s.label} sx={{ ...cardSx, height: '100%' }}>
            <CardContent sx={{ py: 2.5, px: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: s.bg, color: s.color, flexShrink: 0 }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="h5" fontWeight={800} sx={{ color: B.dark }}>{s.value}</Typography>
                    {s.extra}
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#8891A5', fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Short fields */}
      {(() => {
        const filled = profileSections.filter((s) => profile[s.key]);
        if (!filled.length) return null;
        const cols = filled.length >= 4 ? 4 : filled.length;
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(cols, 2)}, 1fr)`, md: `repeat(${cols}, 1fr)` }, gap: 2 }}>
            {filled.map((section) => (
              <Card key={section.key} sx={{ ...cardSx, height: '100%' }}>
                <CardContent sx={{ py: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Box sx={{ color: section.color, display: 'flex' }}>{section.icon}</Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                      {section.label}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                    {profile[section.key]}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        );
      })()}

      {/* Text sections */}
      {textSections.map((section) => {
        const value = profile[section.key];
        if (!value) return null;
        return (
          <Card key={section.key} sx={cardSx}>
            <CardContent sx={{ py: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${section.color}15`, color: section.color,
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
                  {section.label}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ color: '#475569', whiteSpace: 'pre-line', lineHeight: 1.7, pl: 0.5 }}>
                {value}
              </Typography>
            </CardContent>
          </Card>
        );
      })}

      {/* Reviews */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FEF3C7', color: '#F59E0B' }}>
              <StarIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
              Відгуки
            </Typography>
            {profile.reviews?.length > 0 && (
              <Chip label={profile.reviews.length} size="small" sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 700, fontSize: 12 }} />
            )}
            {!showForm && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setEditingReviewId(null); setReviewForm(emptyForm); setShowForm(true); }}
                sx={{ ml: 'auto !important', textTransform: 'none', color: B.primary, fontWeight: 600, borderRadius: 3 }}
              >
                Залишити відгук
              </Button>
            )}
          </Stack>

          {/* Review form */}
          {showForm && (
            <Box sx={{ mb: 2.5, p: 2, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 1.5 }}>
                {editingReviewId ? 'Редагувати відгук' : 'Новий відгук'}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Ім'я учня"
                  size="small"
                  fullWidth
                  value={reviewForm.studentName}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, studentName: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />
                <Box>
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5', mb: 0.5, display: 'block' }}>Оцінка</Typography>
                  <Rating
                    value={reviewForm.rating}
                    onChange={(_, newValue) => setReviewForm((prev) => ({ ...prev, rating: newValue || 1 }))}
                  />
                </Box>
                <TextField
                  label="Текст відгуку"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  value={reviewForm.text}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, text: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    onClick={handleCancelForm}
                    sx={{ textTransform: 'none', color: '#8891A5', borderRadius: 3 }}
                  >
                    Скасувати
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitReview}
                    disabled={addMutation.isPending || updateMutation.isPending}
                    sx={{ textTransform: 'none', bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, fontWeight: 700, px: 3 }}
                  >
                    {editingReviewId ? 'Зберегти' : 'Додати'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {profile.reviews?.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <ReviewSortControls value={reviewSort} onChange={setReviewSort} />
            </Box>
          )}

          {(!profile.reviews || profile.reviews.length === 0) ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#8891A5' }}>Відгуків поки немає.</Typography>
            </Box>
          ) : (
            <Stack spacing={2} divider={<Divider sx={{ borderColor: '#F1F5F9' }} />}>
              {sortReviews(profile.reviews, reviewSort).map((review) => (
                <Box key={review.id}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${B.primary}15`, color: B.primary, fontSize: 13, fontWeight: 700 }}>
                      {review.studentName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                      {review.studentName}
                    </Typography>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="caption" sx={{ color: '#8891A5', ml: 'auto !important' }}>
                      {dayjs(review.createdAt).format('DD.MM.YYYY')}
                    </Typography>
                    {review.parentId === user?.id && (
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => handleEditReview(review)} sx={{ color: B.primary, '&:hover': { bgcolor: '#EDE9FE' } }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#475569', pl: { xs: 0, sm: 5.5 }, lineHeight: 1.6 }}>
                    {review.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ParentTutorProfilePage;
